import React, { useState, useEffect } from 'react';

const DailyReportDownloader = () => {
  const [deviceId, setDeviceId] = useState('');
  const [reportDate, setReportDate] = useState('');
  const [parameters, setParameters] = useState([]);
  const [selectedParameters, setSelectedParameters] = useState([]);
  const [devices, setDevices] = useState([]);
  const [availableParameters, setAvailableParameters] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchingDevices, setFetchingDevices] = useState(true);
  const [fetchingParameters, setFetchingParameters] = useState(false);
  const [error, setError] = useState('');
  const [useManualInput, setUseManualInput] = useState(false);
  const [showParameterSelection, setShowParameterSelection] = useState(false);

  // Fetch devices on component mount
  useEffect(() => {
    const fetchDevices = async () => {
      setFetchingDevices(true);
      try {
        // Updated to use the correct backend endpoint
        const response = await fetch('/api/get-report-devices/', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();

        console.log('Device response:', data);

        if (data && Array.isArray(data)) {
          setDevices(data);
        } else {
          setDevices([]);
          console.error('No devices found');
        }
      } catch (err) {
        setDevices([]);
        setError('Failed to load devices. You can enter device ID manually.');
        console.error('Error fetching devices:', err);
      } finally {
        setFetchingDevices(false);
      }
    };

    fetchDevices();
  }, []);

  // Fetch available parameters
  useEffect(() => {
    const fetchParameters = async () => {
      setFetchingParameters(true);
      try {
        const response = await fetch('/api/get-available-parameters/', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();

        console.log('Parameters response:', data);
        setAvailableParameters(data);
        
        // Set default selected parameters
        const defaultParams = Object.keys(data);
        setSelectedParameters(defaultParams);
      } catch (err) {
        console.error('Error fetching parameters:', err);
        // Set fallback parameters if API fails
        const fallbackParams = [
          "temperature", "relative_humidity", "wind_speed", 
          "wind_direction", "baro_press", "rain", "battery_voltage"
        ];
        setSelectedParameters(fallbackParams);
      } finally {
        setFetchingParameters(false);
      }
    };

    fetchParameters();
  }, []);

  const handleParameterChange = (parameter) => {
    setSelectedParameters(prev => 
      prev.includes(parameter) 
        ? prev.filter(p => p !== parameter)
        : [...prev, parameter]
    );
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    
    if (!deviceId || !reportDate) {
      setError('Please provide both a device ID and date.');
      return;
    }

    if (selectedParameters.length === 0) {
      setError('Please select at least one parameter to include in the report.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Requesting report for device:', deviceId, 'date:', reportDate, 'parameters:', selectedParameters);
      
      // Updated to use the correct backend endpoint with proper payload structure
      const response = await fetch('/api/generate-report/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          device_id: deviceId, 
          report_date: reportDate,
          parameters: selectedParameters
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
      }

      // FIXED: Get the blob directly from the response
      const blob = await response.blob();

      // Verify the blob has content and is likely a PDF
      if (blob.size === 0) {
        throw new Error('Received empty file from server');
      }

      // Check if the blob type is PDF (optional verification)
      console.log('Blob type:', blob.type, 'Blob size:', blob.size);

      // Create download link - FIXED: Use the blob directly
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${deviceId}_${reportDate}.pdf`);
      
      // Force download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      window.URL.revokeObjectURL(url);
      
      // Clear any previous errors on successful download
      setError('');
    } catch (err) {
      let errorMessage = 'Failed to download report. Please try again later.';
      
      if (err.response) {
        console.log('Error response status:', err.response.status);
        console.log('Error response data:', err.response.data);
        
        // Handle different response types
        if (err.response.data instanceof Blob) {
          try {
            const jsonText = await new Response(err.response.data).text();
            const errorData = JSON.parse(jsonText);
            errorMessage = errorData.detail || errorMessage;
          } catch (e) {
            console.error('Error parsing blob error response:', e);
            if (err.response.status === 404) {
              errorMessage = 'Report endpoint not found. Please check the API configuration.';
            }
          }
        } else if (err.response.data && err.response.data.detail) {
          errorMessage = err.response.data.detail;
        } else if (err.response.status === 404) {
          errorMessage = 'Report endpoint not found. Please check the API configuration.';
        } else if (err.response.status === 403) {
          errorMessage = 'You do not have permission to access this device.';
        } else if (err.response.status === 500) {
          errorMessage = 'Server error occurred while generating the report.';
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      console.error('Error downloading report:', err);
    } finally {
      setLoading(false);
    }
  };

  // Format today's date as YYYY-MM-DD for the max date attribute
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="p-6 max-w-2xl mx-auto bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-6 text-center">Generate Weather Reports</h1>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 rounded border border-red-300">
          <strong>Error:</strong> {error}
        </div>
      )}
      
      <form onSubmit={handleDownload} className="space-y-6">
        {/* Device Selection */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="deviceSelect" className="block text-sm font-medium text-gray-700">
              Device ID
            </label>
            <button
              type="button"
              onClick={() => setUseManualInput(!useManualInput)}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              {useManualInput ? "Use Dropdown" : "Enter Manually"}
            </button>
          </div>
          
          {!useManualInput && devices.length > 0 ? (
            <select 
              value={deviceId} 
              onChange={(e) => setDeviceId(e.target.value)}
              disabled={fetchingDevices || devices.length === 0}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a device</option>
              {devices.map((device) => (
                <option key={device.device_id} value={device.device_id}>
                  {device.device_id} - {device.location}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              id="deviceInput"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              placeholder="Enter device ID (e.g., DEVICE001)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            />
          )}
          
          {fetchingDevices && !useManualInput && (
            <p className="mt-1 text-sm text-gray-500">Loading devices...</p>
          )}
        </div>
        
        {/* Date Selection */}
        <div>
          <label htmlFor="dateSelect" className="block text-sm font-medium text-gray-700 mb-1">
            Select Date
          </label>
          <input
            id="dateSelect"
            type="date"
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
            max={today}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
          />
        </div>

        {/* Parameter Selection */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Parameters to Include
            </label>
            <button
              type="button"
              onClick={() => setShowParameterSelection(!showParameterSelection)}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              {showParameterSelection ? "Hide Options" : "Customize Parameters"}
            </button>
          </div>
          
          {showParameterSelection && (
            <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
              {fetchingParameters ? (
                <p className="text-sm text-gray-500">Loading parameters...</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(availableParameters).map(([key, info]) => (
                    <label key={key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedParameters.includes(key)}
                        onChange={() => handleParameterChange(key)}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">
                        {info.display_name} {info.unit}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              
              <div className="mt-3 flex space-x-2">
                <button
                  type="button"
                  onClick={() => setSelectedParameters(Object.keys(availableParameters))}
                  className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedParameters([])}
                  className="text-xs px-2 py-1 bg-gray-100 text-gray-800 rounded hover:bg-gray-200"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
          
          {!showParameterSelection && (
            <p className="text-sm text-gray-600">
              {selectedParameters.length} parameter{selectedParameters.length !== 1 ? 's' : ''} selected
            </p>
          )}
        </div>
        
        {/* Submit Button */}
        <button
          type="submit"
          className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed"
          disabled={loading || !deviceId || !reportDate || selectedParameters.length === 0}
        >
          {loading ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Generating Report...
            </span>
          ) : (
            'Download PDF Report'
          )}
        </button>
      </form>

      {/* Debug Info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-6 p-3 bg-gray-100 rounded text-xs">
          <p><strong>Debug Info:</strong></p>
          <p>Devices loaded: {devices.length}</p>
          <p>Selected device: {deviceId}</p>
          <p>Selected date: {reportDate}</p>
          <p>Selected parameters: {selectedParameters.join(', ')}</p>
        </div>
      )}
    </div>
  );
};

export default DailyReportDownloader;
